import type { ReactNode } from "react";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react";
import {
  Box,
  Flex,
  HoverCard,
  Link,
  Reset,
  Text,
  Tooltip,
} from "@radix-ui/themes";

import { cn } from "~/v2/utils/cn";
import { priceTick } from "~/v3-poc/components/LivePrice";
import "~/v3-poc/components/LivePrice/livePrice.css";
import type { LegQuoteSide } from "../../shared/optionLegs/optionLeg.types";
import type { ChainMetric, VisibleMetrics } from "./optionsChain.columns";
import type { FocusCell } from "./optionsChain.focus";
import { cellAddress, nextFocusCell } from "./optionsChain.focus";
import type {
  ChainLeg,
  ChainRow,
  ChainSection,
  ChainSide,
  ChainSideFilter,
  PinEdge,
  ValueFreshness,
} from "./optionsChain.types";
import { formatPrice, formatStrike, MISSING_VALUE } from "./optionsChain.utils";
import { scrollTopForStrike } from "./optionsChain.visual";

/**
 * One vertical scroller holding three columns: call metrics, the pin, put
 * metrics.
 *
 * Rows cannot drift apart, because only one element scrolls vertically. The pin
 * is the centre column — call bid and ask, the strike, put bid and ask — and it
 * never scrolls horizontally, so the only actionable cells stay in the widget
 * while each side's metrics slide in their own horizontal panes. Those panes are
 * kept in step by a `scrollLeft` write, where a frame of lag is far less visible
 * than rows splitting.
 *
 * Every row is exactly `ROW_HEIGHT` in all three columns, which is what keeps
 * them in line without measuring anything.
 *
 * Plain table elements rather than Radix `Table.*`: the alignment guarantee needs
 * an exact row height, and the Radix padding scale cannot promise one.
 */
const ROW_HEIGHT = 28;
const GROUP_HEADER_HEIGHT = 24;
const COLUMN_HEADER_HEIGHT = 26;
const HEADER_HEIGHT = GROUP_HEADER_HEIGHT + COLUMN_HEADER_HEIGHT;
const STRIKE_WIDTH_REM = 5.5;

/**
 * Each side's metrics scroll horizontally on their own, and only horizontally:
 * `overflow-y: clip` keeps a pane from becoming a vertical scroller, which would
 * take the shared header and bands out of the one scroller they stick to.
 * `overscroll-x-none` is per-axis on purpose — blocking both axes would stop a
 * vertical gesture over a pane from reaching the chain behind it.
 */
const METRIC_H_SCROLL_CLASS =
  "min-w-0 overflow-x-auto overflow-y-clip overscroll-x-none [scrollbar-width:none]";

const hairlineBorderClass = "border-[color:var(--gray-a5)]";

/**
 * A row inside a section's ladder. The band is not one of these: it is the
 * section's own header row, rendered once per group.
 */
type VisualRow =
  | { kind: "spot"; key: string }
  | {
      kind: "strike";
      key: string;
      row: ChainRow;
      strikeIndex: number;
      /** The row's own expiration, which names the contract a quote would buy. */
      expirationLabel: string;
    };

/**
 * One expiration as its own row group.
 *
 * Each section becomes a `<tbody>`, which is what makes the band and the price
 * stick: a sticky row is held inside its own row group, so it pins while that
 * ladder is on screen and is pushed out by the next one — several at a time,
 * without anything floating above the table pretending to be a row.
 *
 * Row heights are uniform, so a section's extent is arithmetic: `start` is its
 * first row in the flattened order and `length` counts every row it renders.
 */
interface VisualSection {
  expiration: string;
  label: string;
  isCollapsed: boolean;
  withBand: boolean;
  rows: VisualRow[];
  start: number;
  length: number;
}

/**
 * Where the underlying price falls in an ascending ladder: after every strike
 * below it, before the rest.
 */
const spotRowIndex = (rows: readonly ChainRow[], spot: number) => {
  const above = rows.findIndex((row) => row.strike > spot);
  return above === -1 ? rows.length : above;
};

const toVisualSections = (
  sections: readonly ChainSection[],
  collapsed: ReadonlySet<string>,
  spot: number | undefined,
): VisualSection[] => {
  // A single expiration is already named by the picker, so a band would only
  // repeat it and cost a row — and there would be nothing to collapse it
  // against.
  const withBand = sections.length > 1;
  // Counted over rendered rows only, so keyboard navigation steps across a
  // collapsed section rather than into it.
  let strikeIndex = 0;
  let start = 0;

  return sections.map((section) => {
    const isCollapsed = withBand && collapsed.has(section.expiration);
    const rows: VisualRow[] = [];

    if (!isCollapsed) {
      // Every ladder marks its own crossing. The money cuts each expiration in
      // a different place, so one marker for the whole chain is right about the
      // ladder it was placed in and wrong about the rest.
      const spotAt = spot === undefined ? -1 : spotRowIndex(section.rows, spot);
      section.rows.forEach((row, index) => {
        if (index === spotAt) {
          rows.push({ kind: "spot", key: `spot-${section.expiration}` });
        }
        rows.push({
          kind: "strike",
          key: `${section.expiration}-${row.strike}`,
          row,
          strikeIndex,
          expirationLabel: section.label,
        });
        strikeIndex += 1;
      });
      // Spot above every strike in this ladder, so the marker closes it out.
      if (spotAt === section.rows.length) {
        rows.push({ kind: "spot", key: `spot-${section.expiration}` });
      }
    }

    const length = rows.length + (withBand ? 1 : 0);
    const visual: VisualSection = {
      expiration: section.expiration,
      label: section.label,
      isCollapsed,
      withBand,
      rows,
      start,
      length,
    };
    start += length;
    return visual;
  });
};

/**
 * The sections overlapping the viewport, with the row counts standing in for
 * those above and below.
 *
 * Whole sections rather than individual rows: a sticky row only holds its place
 * while its own row group is mounted, so a section is either rendered complete
 * or replaced by a spacer. Their extent is bounded by the strike count, so this
 * still keeps the mounted rows proportional to what is on screen.
 */
export interface SectionRange {
  /** Both inclusive; `startIndex` is -1 when nothing overlaps. */
  startIndex: number;
  endIndex: number;
}

/**
 * Which sections overlap the viewport.
 *
 * Split from building the window so a scroll can ask the cheap question — has
 * the mounted set changed — without producing a new window object. Answering it
 * from the scroll listener is what keeps a gesture from re-rendering the grid
 * on every row it crosses.
 */
const sectionRange = (
  sections: readonly VisualSection[],
  scrollTop: number,
  viewportHeight: number,
): SectionRange => {
  const first = scrollTop / ROW_HEIGHT - SECTION_OVERSCAN_ROWS;
  const last =
    (scrollTop + viewportHeight) / ROW_HEIGHT + SECTION_OVERSCAN_ROWS;
  let startIndex = -1;
  let endIndex = -1;
  for (const [index, section] of sections.entries()) {
    if (section.start + section.length > first && section.start < last) {
      if (startIndex === -1) {
        startIndex = index;
      }
      endIndex = index;
    }
  }
  return { startIndex, endIndex };
};

const windowSections = (
  sections: readonly VisualSection[],
  range: SectionRange,
) => {
  const visible =
    range.startIndex === -1
      ? []
      : sections.slice(range.startIndex, range.endIndex + 1);
  const total = sections.reduce((rows, section) => rows + section.length, 0);
  const leading = visible[0]?.start ?? total;
  const lastVisible = visible.at(-1);
  const trailing = lastVisible
    ? total - (lastVisible.start + lastVisible.length)
    : 0;
  return { visible, leadingRows: leading, trailingRows: trailing, total };
};

/** Rows kept mounted beyond the viewport, so a scroll does not reveal a gap. */
const SECTION_OVERSCAN_ROWS = 6;

type StrikeRow = Extract<VisualRow, { kind: "strike" }>;

/**
 * A section's rows, cut at the price marker.
 *
 * In a metrics pane the strikes sit inside a horizontal scroller and the marker
 * must not: it stays pinned to the top of the chain, and a sticky element inside
 * a scroller sticks to that scroller instead. So each run of strikes becomes its
 * own scroller and the marker is rendered between them.
 */
type RowChunk =
  | { kind: "spot"; key: string }
  | { kind: "strikes"; key: string; rows: StrikeRow[] };

const chunkSectionRows = (rows: readonly VisualRow[]): RowChunk[] => {
  const chunks: RowChunk[] = [];
  for (const item of rows) {
    if (item.kind === "spot") {
      chunks.push({ kind: "spot", key: item.key });
      continue;
    }
    const open = chunks.at(-1);
    if (open?.kind === "strikes") {
      open.rows.push(item);
    } else {
      chunks.push({
        kind: "strikes",
        key: `strikes-${item.key}`,
        rows: [item],
      });
    }
  }
  return chunks;
};

/**
 * The underlying price, drawn as a rule across the chain broken by the number.
 *
 * The number sits in the strike column, because that is the axis it is a value
 * on: read against the strikes it falls between, whether that column is in the
 * middle with both sides up or leading when only one is. The side panes draw
 * the rule alone, so the line reads as one across the chain rather than the
 * same figure printed three times.
 */
const SpotRule = ({
  spot,
  showsPrice,
}: {
  spot: number | undefined;
  showsPrice: boolean;
}) =>
  showsPrice ? (
    <Flex align="center" justify="center" height="100%" px="1">
      <Text size="1" weight="bold" className="atd-numeric">
        {spot === undefined ? MISSING_VALUE : formatPrice(spot)}
      </Text>
    </Flex>
  ) : (
    <Flex align="center" height="100%" px="2">
      <Box className="h-px flex-1 bg-[var(--gray-a5)]" />
    </Flex>
  );

/** Opaque, because a stuck row is painted over the rows passing beneath it. */
const SPOT_MARKER_BACKGROUND = "var(--color-panel-solid)";
/**
 * Opaque for the same reason, but the band's own tint is layered back on so a
 * parked band still reads as the grey rule it replaces.
 */
const STUCK_BAND_BACKGROUND =
  "linear-gradient(var(--gray-a2), var(--gray-a2)), var(--color-panel-solid)";

/**
 * At-the-money marker, drawn per cell rather than as an outline on the row.
 *
 * The pinned bid and ask cells have an opaque background and their own stacking
 * context, so they paint over anything the `<tr>` draws. An inset shadow is part
 * of each cell's own painting, so every column carries its own segment. Unlike a
 * border it also adds no height, which matters because the three panes stay
 * aligned only while every row is exactly `ROW_HEIGHT`.
 */
const ATM_MARKER =
  "inset 0 1px 0 var(--accent-a7), inset 0 -1px 0 var(--accent-a7)";

/**
 * Header cells carry explicit heights for the same reason rows do: the three
 * panes must agree to the pixel, and a control inside a cell (the strike sort
 * button) otherwise makes its row a pixel taller than the others.
 */
const groupHeaderCell: React.CSSProperties = {
  height: GROUP_HEADER_HEIGHT,
  boxSizing: "border-box",
  lineHeight: 1,
};
const columnHeaderCell: React.CSSProperties = {
  height: COLUMN_HEADER_HEIGHT,
  boxSizing: "border-box",
  lineHeight: 1,
};
/** Opaque, so scrolled columns cannot show through a pinned cell. */
const pinnedBackground = "var(--color-panel-solid)";

/** Anything the feed is not delivering live is dimmed. */
const freshnessColor = (freshness: ValueFreshness) =>
  freshness === "live" ? undefined : ("gray" as const);

export interface QuoteCellSelection {
  symbol: string;
  side: LegQuoteSide;
}

export interface OptionsChainGridProps {
  /** One stacked expiration each, in display order. */
  sections: readonly ChainSection[];
  metrics: VisibleMetrics;
  symbol: string;
  /** Per-metric freshness, computed once by the connector. */
  freshnessByMetric: Readonly<Record<string, ValueFreshness>>;
  canTrade: boolean;
  /**
   * A leg is routed to a ticket in the chain's own group, so a chain without one
   * has nowhere to send an order and its quotes are read-only.
   */
  hasGroup: boolean;
  selected: readonly QuoteCellSelection[];
  /**
   * True when the target ticket already holds the maximum number of legs for the
   * symbol this grid is showing. A quote for a contract that is not already on
   * the ticket cannot be added, so it is rendered as a value rather than an
   * action.
   */
  isTicketFull: boolean;
  /**
   * Confirmed indicative feed. Marks the columns whose source carries the
   * 15-minute delay, which is a fact about the source — the age of any individual
   * value is mostly liquidity and is left to the freshness treatment.
   */
  isDelayedFeed: boolean;
  /** Upgrade path offered on the delayed column headers. */
  realTimeHref: string;
  /** Options application, offered on quotes the account cannot act on. */
  applyHref: string;
  /** No Greeks published for this expiration, noted on the columns it empties. */
  isGreeksUnavailable: boolean;
  /** No underlying price, so no at-the-money or in-the-money treatment. */
  isSpotUnavailable: boolean;
  /** Underlying price, marked in the ladder and pinned on screen. */
  spot?: number;
  /** Which side to render. Hiding one gives the other the full width. */
  sideFilter: ChainSideFilter;
  onQuoteSelect: (
    leg: ChainLeg,
    price: number | undefined,
    quoteSide: LegQuoteSide,
  ) => void;
}

const quoteSideOf = (metricId: string): LegQuoteSide | undefined =>
  metricId === "bid" ? "bid" : metricId === "ask" ? "ask" : undefined;

/** Sign colouring for net change and change percent. */
const signedColor = (
  value: number | undefined,
): "green" | "red" | undefined => {
  if (value === undefined || value === 0) {
    return undefined;
  }
  return value > 0 ? "green" : "red";
};

const signedValueOf = (metric: ChainMetric, leg: ChainLeg | undefined) => {
  if (!leg || !metric.isSigned) {
    return undefined;
  }
  return metric.id === "netChange"
    ? leg.quote.netChange
    : leg.quote.changePercent;
};

/**
 * Header for a column the feed delays, carrying the explanation and the upgrade
 * link. A hover card rather than a tooltip because the link has to be clickable:
 * a tooltip closes the moment the pointer leaves the trigger.
 */
const DelayedHeaderLabel = ({
  label,
  realTimeHref,
}: {
  label: string;
  realTimeHref: string;
}) => (
  <HoverCard.Root openDelay={300}>
    <HoverCard.Trigger>
      <Text
        size="1"
        color="orange"
        weight="medium"
        tabIndex={0}
        className="cursor-help"
      >
        {`${label} (`}
        <Text size="1" color="red" weight="bold">
          !
        </Text>
        {")"}
      </Text>
    </HoverCard.Trigger>
    <HoverCard.Content size="1" maxWidth="260px">
      <Text as="p" size="1">
        Indicative prices. The bid and ask are approximate. Trades and volume
        are 15 minutes behind.{" "}
        <Link
          size="1"
          href={realTimeHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          Get real-time
        </Link>
      </Text>
    </HoverCard.Content>
  </HoverCard.Root>
);

/**
 * Wraps a disabled quote so the reason is discoverable from the cell the user
 * just tried to click.
 */
const ReadOnlyQuoteHoverCard = ({
  explanation,
  children,
}: {
  explanation: ReactNode;
  children: ReactNode;
}) => (
  <HoverCard.Root openDelay={300}>
    <HoverCard.Trigger>
      <span className="inline-flex">{children}</span>
    </HoverCard.Trigger>
    <HoverCard.Content size="1" maxWidth="260px">
      <Text as="p" size="1">
        {explanation}
      </Text>
    </HoverCard.Content>
  </HoverCard.Root>
);

/**
 * Only a read-only chain needs the wrapper, so a tradable one pays nothing. The
 * account outranks the group: no application, no order, whatever the widget is
 * linked to.
 */
const maybeExplainReadOnly = (
  canTrade: boolean,
  hasGroup: boolean,
  applyHref: string,
  cell: ReactNode,
) => {
  if (!canTrade) {
    return (
      <ReadOnlyQuoteHoverCard
        explanation={
          <>
            This account cannot trade options yet, so prices are read-only.{" "}
            <Link
              size="1"
              href={applyHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Apply for options trading"
            >
              Apply
            </Link>
          </>
        }
      >
        {cell}
      </ReadOnlyQuoteHoverCard>
    );
  }
  if (!hasGroup) {
    return (
      <ReadOnlyQuoteHoverCard explanation="This chain has no group to route an order to, so prices are read-only.">
        {cell}
      </ReadOnlyQuoteHoverCard>
    );
  }
  return cell;
};

/**
 * One actionable quote. Carries its full context in the accessible name because
 * a chain has hundreds of these and "1.10" alone is useless out of context.
 */
const QuoteCell = ({
  leg,
  metric,
  side,
  strike,
  expirationLabel,
  isSelected,
  isFocused,
  hasMoved,
  isActionable,
  address,
  onSelect,
  onFocus,
}: {
  leg: ChainLeg | undefined;
  metric: ChainMetric;
  side: ChainSide;
  strike: number;
  expirationLabel: string;
  isSelected: boolean;
  isFocused: boolean;
  /** Whether this price just moved, in either direction. */
  hasMoved: boolean;
  /** False when the quote cannot become a leg, for any reason. */
  isActionable: boolean;
  /** Lets the grid re-find this button after virtualization remounts its row. */
  address: string;
  onSelect: () => void;
  onFocus: () => void;
}) => {
  const quoteSide = quoteSideOf(metric.id);

  if (!leg) {
    return (
      <Text size="1" color="gray" aria-label={`${metric.label} unavailable`}>
        {MISSING_VALUE}
      </Text>
    );
  }

  const value = metric.format(leg.quote);
  const price = quoteSide === "bid" ? leg.quote.bid : leg.quote.ask;
  const hasPrice = price !== undefined;

  const label = [
    side === "call" ? "Call" : "Put",
    metric.label.toLowerCase(),
    hasPrice ? value : "no price",
    `strike ${formatStrike(strike)}`,
    expirationLabel,
  ]
    .filter(Boolean)
    .join(", ");

  // No price means there is nothing to render as a price at all — a one-sided
  // quote must never look clickable, because a user cannot sell into a bid that
  // does not exist.
  if (!hasPrice || !quoteSide) {
    return (
      <Text size="1" color="gray" className="atd-numeric" aria-label={label}>
        {value}
      </Text>
    );
  }

  // A quote that exists but cannot be acted on stays a button and is disabled,
  // rather than becoming text: a button and a span differ in height and padding,
  // so swapping them would shift every row around it whenever a ticket fills up.
  //
  // A chain cell is denser than any Radix `Button` size, so it is one of the few
  // controls that stays a native element — with `Reset` for the teardown and
  // `Text` for the label, so size and colour still come from the theme.
  return (
    <Reset>
      <button
        type="button"
        data-chain-cell={address}
        disabled={!isActionable}
        aria-pressed={isSelected}
        aria-label={label}
        tabIndex={isFocused && isActionable ? 0 : -1}
        onFocus={onFocus}
        onClick={onSelect}
        className={cn(
          // Soft-filled and borderless, matching the buy/sell cells in the
          // design system's order tables. A quote already on the ticket steps
          // up the same hue and takes a solid rule: an inset outline reads at a
          // glance without the height change a border would add.
          "block w-full rounded-[var(--radius-1)] p-px px-1.5 text-center",
          "focus-visible:shadow-[inset_0_0_0_2px_var(--focus-8)]",
          isActionable ? "cursor-pointer" : "cursor-default",
          !isActionable && "bg-[var(--gray-a3)]",
          isSelected && "outline outline-1 -outline-offset-1",
          isActionable &&
            (quoteSide === "bid"
              ? isSelected
                ? "bg-[var(--red-a6)] outline-[var(--red-9)]"
                : "bg-[var(--red-a3)] hover:bg-[var(--red-a4)]"
              : isSelected
                ? "bg-[var(--green-a6)] outline-[var(--green-9)]"
                : "bg-[var(--green-a3)] hover:bg-[var(--green-a4)]"),
        )}
      >
        <Text
          key={hasMoved ? value : "steady"}
          size="1"
          weight="bold"
          className={cn(
            "atd-numeric",
            hasMoved && "workspace-live-price--lift",
          )}
          color={!isActionable ? "gray" : quoteSide === "bid" ? "red" : "green"}
        >
          {value}
        </Text>
      </button>
    </Reset>
  );
};

/**
 * Column order for one side, left to right: calls run their metrics out to the
 * left of the pin, puts to the right of it. This is the order the keyboard walks,
 * whichever column an individual cell is rendered in.
 */
const orderedMetrics = (
  metrics: VisibleMetrics,
  edge: PinEdge,
): ChainMetric[] =>
  edge === "right"
    ? [...metrics.scrollable, ...metrics.sticky]
    : [...metrics.sticky, ...metrics.scrollable];

/**
 * Which way a side faces the pin. With both sides up the calls run out to the
 * left, so their pinned pair ends the side's order; every other case has the pin
 * leading.
 */
const pinEdgeFor = (side: ChainSide, sideFilter: ChainSideFilter): PinEdge =>
  sideFilter === "both" && side === "call" ? "right" : "left";

/** Where the metrics pane's first column sits in its side's order. */
const paneIndexOffset = (metrics: VisibleMetrics, edge: PinEdge) =>
  edge === "right" ? 0 : metrics.sticky.length;

/** Where bid and ask sit in that same order, now that they live in the pin. */
const pinIndexOffset = (metrics: VisibleMetrics, edge: PinEdge) =>
  edge === "right" ? metrics.scrollable.length : 0;

const widthRemOf = (metrics: readonly ChainMetric[]) =>
  metrics.reduce((total, metric) => total + metric.widthRem, 0);

/**
 * One data cell. Split out of the row so the pane component stays within the
 * complexity budget, and so the decision of whether a quote is actionable lives
 * next to the decision of how to render it.
 */
/**
 * Which sides of this leg are on the ticket, as a value rather than a slice of
 * the selection. A row can then be compared without reaching into an array that
 * is rebuilt whenever anything anywhere is selected.
 */
const selectedSidesFor = (
  selected: readonly QuoteCellSelection[],
  leg: ChainLeg | undefined,
): string => {
  if (!leg) {
    return "";
  }
  let sides = "";
  for (const entry of selected) {
    if (entry.symbol === leg.symbol) {
      sides += `${entry.side},`;
    }
  }
  return sides;
};

/**
 * One strike's metrics on one side, inside that side's horizontal pane.
 *
 * Memoised because the poll rebuilds the chain every few seconds while most
 * strikes are unchanged, and re-rendering all of them is what stalls the main
 * thread. Every prop is therefore either a value or an identity that survives a
 * poll, so an untouched row costs a comparison instead of a subtree.
 */
const MetricsRow = memo(
  ({
    metrics,
    indexOffset,
    leg,
    row,
    rowIndex,
    side,
    expirationLabel,
    freshnessByMetric,
    canTrade,
    hasGroup,
    applyHref,
    isTicketFull,
    selectedSides,
    onQuoteSelect,
    onFocusCell,
  }: {
    metrics: readonly ChainMetric[];
    /** Column index of this pane's first metric within the side's own order. */
    indexOffset: number;
    leg: ChainLeg | undefined;
    row: ChainRow;
    rowIndex: number;
    side: ChainSide;
    expirationLabel: string;
    freshnessByMetric: Readonly<Record<string, ValueFreshness>>;
    canTrade: boolean;
    hasGroup: boolean;
    applyHref: string;
    isTicketFull: boolean;
    selectedSides: string;
    onQuoteSelect: OptionsChainGridProps["onQuoteSelect"];
    onFocusCell: (cell: FocusCell) => void;
  }) => {
    const rowBackground = leg?.isInTheMoney ? "var(--gray-a3)" : undefined;
    return (
      <tr style={{ height: ROW_HEIGHT, background: rowBackground }}>
        {metrics.map((metric, index) => (
          <ChainCell
            key={metric.id}
            applyHref={applyHref}
            metric={metric}
            leg={leg}
            row={row}
            rowIndex={rowIndex}
            metricIndex={indexOffset + index}
            side={side}
            rowBackground={rowBackground}
            expirationLabel={expirationLabel}
            freshness={freshnessByMetric[metric.id] ?? "live"}
            canTrade={canTrade}
            hasGroup={hasGroup}
            isTicketFull={isTicketFull}
            selectedSides={selectedSides}
            // Only bid and ask render a button, and they are in the pin.
            isFocused={false}
            onQuoteSelect={onQuoteSelect}
            onFocusCell={onFocusCell}
          />
        ))}
      </tr>
    );
  },
);

/**
 * One strike in the pin: the pair of quotes for each side on show, with the
 * strike between them.
 *
 * Memoised for the same reason as the metrics rows, and holding both sides
 * because they share a single `<tr>` — the pin is one table so that a band or the
 * price marker can span it.
 */
const PinRow = memo(
  ({
    stickyMetrics,
    sideFilter,
    callIndexOffset,
    putIndexOffset,
    row,
    rowIndex,
    expirationLabel,
    freshnessByMetric,
    canTrade,
    hasGroup,
    applyHref,
    isTicketFull,
    callSelectedSides,
    putSelectedSides,
    focusCell,
    onQuoteSelect,
    onFocusCell,
  }: {
    stickyMetrics: readonly ChainMetric[];
    sideFilter: ChainSideFilter;
    callIndexOffset: number;
    putIndexOffset: number;
    row: ChainRow;
    rowIndex: number;
    expirationLabel: string;
    freshnessByMetric: Readonly<Record<string, ValueFreshness>>;
    canTrade: boolean;
    hasGroup: boolean;
    applyHref: string;
    isTicketFull: boolean;
    callSelectedSides: string;
    putSelectedSides: string;
    focusCell: FocusCell;
    onQuoteSelect: OptionsChainGridProps["onQuoteSelect"];
    onFocusCell: (cell: FocusCell) => void;
  }) => {
    const quotes = (side: ChainSide) => {
      const leg = side === "call" ? row.call : row.put;
      const indexOffset = side === "call" ? callIndexOffset : putIndexOffset;
      // Each pin cell paints its own side's in-the-money tint: the two sides
      // cross the money at the same strike from opposite directions, so one
      // background on the row could only be right about one of them.
      const rowBackground = leg?.isInTheMoney ? "var(--gray-a3)" : undefined;
      return stickyMetrics.map((metric, index) => (
        <ChainCell
          key={`${side}-${metric.id}`}
          applyHref={applyHref}
          metric={metric}
          leg={leg}
          row={row}
          rowIndex={rowIndex}
          metricIndex={indexOffset + index}
          side={side}
          rowBackground={rowBackground}
          expirationLabel={expirationLabel}
          freshness={freshnessByMetric[metric.id] ?? "live"}
          canTrade={canTrade}
          hasGroup={hasGroup}
          isTicketFull={isTicketFull}
          selectedSides={side === "call" ? callSelectedSides : putSelectedSides}
          isFocused={
            focusCell.side === side &&
            focusCell.rowIndex === rowIndex &&
            focusCell.metricIndex === indexOffset + index
          }
          onQuoteSelect={onQuoteSelect}
          onFocusCell={onFocusCell}
        />
      ));
    };

    return (
      <tr
        style={{
          height: ROW_HEIGHT,
          background: row.isAtTheMoney ? "var(--accent-a3)" : undefined,
        }}
      >
        {sideFilter === "both" ? quotes("call") : null}
        <th
          scope="row"
          className={cn("border-x px-1.5 text-center", hairlineBorderClass)}
          style={{ boxShadow: row.isAtTheMoney ? ATM_MARKER : undefined }}
        >
          <Text size="1" weight="medium" className="atd-numeric">
            {formatStrike(row.strike)}
          </Text>
          {row.isAtTheMoney ? (
            <Text className="sr-only"> at the money</Text>
          ) : null}
        </th>
        {sideFilter === "call" ? quotes("call") : quotes("put")}
      </tr>
    );
  },
);

const ChainCell = ({
  metric,
  leg,
  row,
  rowIndex,
  metricIndex,
  side,
  rowBackground,
  expirationLabel,
  freshness,
  canTrade,
  hasGroup,
  applyHref,
  isTicketFull,
  selectedSides,
  isFocused,
  onQuoteSelect,
  onFocusCell,
}: {
  metric: ChainMetric;
  leg: ChainLeg | undefined;
  row: ChainRow;
  rowIndex: number;
  metricIndex: number;
  side: ChainSide;
  rowBackground: string | undefined;
  expirationLabel: string;
  freshness: ValueFreshness;
  canTrade: boolean;
  hasGroup: boolean;
  applyHref: string;
  isTicketFull: boolean;
  selectedSides: string;
  isFocused: boolean;
  onQuoteSelect: OptionsChainGridProps["onQuoteSelect"];
  onFocusCell: (cell: FocusCell) => void;
}) => {
  const quoteSide = quoteSideOf(metric.id);
  const isSelected = !!quoteSide && selectedSides.includes(`${quoteSide},`);
  const previousLeg = side === "call" ? row.previous?.call : row.previous?.put;
  const tick = priceTick(
    previousLeg && metric.value(previousLeg.quote),
    leg && metric.value(leg.quote),
  );
  // A full ticket can still take a quote for a contract already on it, because
  // that click removes or flips the leg.
  const isActionable = canTrade && hasGroup && (!isTicketFull || isSelected);

  return (
    <td
      className={cn(
        "overflow-hidden whitespace-nowrap px-1.5",
        metric.isSticky ? "text-center" : "text-right",
      )}
      style={{
        boxShadow: row.isAtTheMoney ? ATM_MARKER : undefined,
        // A pin cell is opaque, so nothing scrolling past it can show through;
        // the row's own tint is layered back over that.
        ...(metric.isSticky
          ? {
              background: rowBackground
                ? `linear-gradient(${rowBackground}, ${rowBackground}), ${pinnedBackground}`
                : pinnedBackground,
            }
          : {}),
      }}
    >
      {metric.isQuoteAction ? (
        maybeExplainReadOnly(
          canTrade,
          hasGroup,
          applyHref,
          <QuoteCell
            leg={leg}
            metric={metric}
            side={side}
            strike={row.strike}
            expirationLabel={expirationLabel}
            isSelected={isSelected}
            isFocused={isFocused}
            hasMoved={tick !== "flat"}
            isActionable={isActionable}
            address={cellAddress({ rowIndex, side, metricIndex })}
            onSelect={() => {
              if (!leg || !quoteSide) {
                return;
              }
              onQuoteSelect(
                leg,
                quoteSide === "bid" ? leg.quote.bid : leg.quote.ask,
                quoteSide,
              );
            }}
            onFocus={() => onFocusCell({ rowIndex, side, metricIndex })}
          />,
        )
      ) : (
        /*
          Keyed on the text so a new reading remounts and replays the flash; the
          animation only runs on mount.
        */
        <Text
          key={leg ? metric.format(leg.quote) : MISSING_VALUE}
          size="1"
          color={
            metric.isSigned
              ? signedColor(signedValueOf(metric, leg))
              : freshnessColor(freshness)
          }
          className={cn(
            "atd-numeric workspace-live-price",
            tick === "up" && "workspace-live-price--up",
            tick === "down" && "workspace-live-price--down",
          )}
        >
          {leg ? metric.format(leg.quote) : MISSING_VALUE}
        </Text>
      )}
    </td>
  );
};

/** Parked under the header while its own ladder is on screen. */
const bandStickyStyle: React.CSSProperties = {
  position: "sticky",
  top: HEADER_HEIGHT,
  zIndex: 3,
  background: STUCK_BAND_BACKGROUND,
};

/**
 * Held at both edges, so the money stays on screen for as long as its own ladder
 * does and then leaves with it.
 */
const spotStickyStyle = (withBand: boolean): React.CSSProperties => ({
  position: "sticky",
  top: HEADER_HEIGHT + (withBand ? ROW_HEIGHT : 0),
  bottom: 0,
  zIndex: 2,
  background: SPOT_MARKER_BACKGROUND,
});

/**
 * The band's control, repeated in every column so the rule reads as one row
 * across the chain. Only the column that names it is reachable.
 */
const BandControl = ({
  label,
  isCollapsed,
  showsLabel,
  onToggle,
}: {
  label: string;
  isCollapsed: boolean;
  showsLabel: boolean;
  onToggle: () => void;
}) => (
  <Reset>
    <button
      type="button"
      aria-hidden={!showsLabel}
      tabIndex={showsLabel ? 0 : -1}
      aria-expanded={showsLabel ? !isCollapsed : undefined}
      aria-label={showsLabel ? `${label} contracts` : undefined}
      onClick={onToggle}
      className="flex h-full w-full cursor-pointer items-center px-1.5 focus-visible:shadow-[inset_0_0_0_2px_var(--focus-8)]"
    >
      {showsLabel ? (
        <Flex align="center" gap="1">
          {isCollapsed ? (
            <CaretUpIcon size={11} aria-hidden />
          ) : (
            <CaretDownIcon size={11} aria-hidden />
          )}
          <Text size="1" weight="medium" color="gray">
            {label}
          </Text>
        </Flex>
      ) : null}
    </button>
  </Reset>
);

/**
 * One side's scrollable metrics.
 *
 * The pane itself is not a scroller: each run of strikes is, so that the header,
 * the bands, and the price marker can stay outside them and stick to the chain's
 * own scroller. Those runs are kept level with one another, and with the other
 * side, by the grid's `scrollLeft` sync.
 */
const MetricsPane = ({
  side,
  window,
  spot,
  showsSectionLabel,
  onToggleSection,
  columns,
  indexOffset,
  isCompactHeader,
  symbol,
  freshnessByMetric,
  canTrade,
  hasGroup,
  applyHref,
  selected,
  isTicketFull,
  isDelayedFeed,
  realTimeHref,
  isGreeksUnavailable,
  onQuoteSelect,
  onFocusCell,
}: {
  side: ChainSide;
  window: ReturnType<typeof windowSections>;
  spot: number | undefined;
  /** Whether this pane prints the expiration text. */
  showsSectionLabel: boolean;
  onToggleSection: (expiration: string) => void;
  /** This side's scrollable metrics: bid and ask live in the pin. */
  columns: readonly ChainMetric[];
  /** Where the first of them sits in the side's own column order. */
  indexOffset: number;
  isCompactHeader: boolean;
  symbol: string;
  freshnessByMetric: Readonly<Record<string, ValueFreshness>>;
  canTrade: boolean;
  hasGroup: boolean;
  applyHref: string;
  selected: readonly QuoteCellSelection[];
  isTicketFull: boolean;
  isDelayedFeed: boolean;
  realTimeHref: string;
  isGreeksUnavailable: boolean;
  onQuoteSelect: OptionsChainGridProps["onQuoteSelect"];
  onFocusCell: (cell: FocusCell) => void;
}) => {
  const groupLabel = side === "call" ? "Calls" : "Puts";
  /**
   * Every table in this pane is laid out from the same widths and cannot be
   * narrower than their sum, which is what keeps the header level with the runs
   * of strikes below it however far they are scrolled.
   */
  const minWidth = `${widthRemOf(columns)}rem`;
  const columnWidths = (
    <colgroup>
      {columns.map((metric) => (
        // Column width is config-driven per metric.
        <col key={metric.id} style={{ width: `${metric.widthRem}rem` }} />
      ))}
    </colgroup>
  );

  const header = (
    <thead>
      {/*
          The side is named in the pin, over the pair the name belongs to. This
          row only carries the rule on out to the edge of the widget.
        */}
      <tr style={{ height: GROUP_HEADER_HEIGHT }}>
        <th
          aria-hidden
          colSpan={columns.length}
          className={cn("border-t border-b", hairlineBorderClass)}
          style={{ ...groupHeaderCell, background: pinnedBackground }}
        />
      </tr>
      <tr style={{ height: COLUMN_HEADER_HEIGHT }}>
        {columns.map((metric) => (
          <th
            key={metric.id}
            scope="col"
            className={cn(
              "whitespace-nowrap border-b px-1.5 text-right",
              hairlineBorderClass,
            )}
            style={{ ...columnHeaderCell, background: pinnedBackground }}
          >
            {isDelayedFeed && metric.transport === "delayed" ? (
              <DelayedHeaderLabel
                label={isCompactHeader ? metric.shortLabel : metric.label}
                realTimeHref={realTimeHref}
              />
            ) : isGreeksUnavailable && metric.transport === "computed" ? (
              <Tooltip content="Not published for this expiration.">
                <Text
                  size="1"
                  color="gray"
                  weight="medium"
                  tabIndex={0}
                  className="cursor-help"
                >
                  {isCompactHeader ? metric.shortLabel : metric.label}
                </Text>
              </Tooltip>
            ) : (
              <Text size="1" color="gray" weight="medium">
                {isCompactHeader ? metric.shortLabel : metric.label}
              </Text>
            )}
          </th>
        ))}
      </tr>
    </thead>
  );

  /*
    One box per expiration, which is what scopes the sticky rows: a sticky
    element is held inside its own containing block, so a band pins while its
    ladder is on screen and is pushed out by the next one.

    Inside a section, only the runs of strikes scroll horizontally. The band and
    the price marker are their own boxes above and between them, because they
    stick to the chain's scroller and a sticky element inside a scroller would
    stick to that instead.
  */
  return (
    <Box data-chain-metrics={side} className="min-w-0 overflow-x-clip">
      <div
        data-chain-h-scroll
        data-chain-metrics-head
        className={cn(METRIC_H_SCROLL_CLASS, "sticky top-0 z-[4]")}
      >
        <table
          className="w-full table-fixed border-separate border-spacing-0"
          style={{ minWidth }}
        >
          <caption className="sr-only">
            {groupLabel} for {symbol}
          </caption>
          {columnWidths}
          {header}
        </table>
      </div>

      {window.leadingRows > 0 ? (
        <div aria-hidden style={{ height: window.leadingRows * ROW_HEIGHT }} />
      ) : null}

      {window.visible.map((section) => (
        <div key={section.expiration} data-chain-section={section.expiration}>
          {section.withBand ? (
            /*
              The whole band folds its ladder away, in whichever column it is
              clicked. Only the outermost column carries the label and the
              caret; the others repeat the band so it reads as one rule across
              the chain, and are hidden from assistive tech so the control is
              announced once.
            */
            <div
              className={cn("border-b", hairlineBorderClass)}
              style={{ height: ROW_HEIGHT, ...bandStickyStyle }}
            >
              <BandControl
                label={section.label}
                isCollapsed={section.isCollapsed}
                showsLabel={showsSectionLabel}
                onToggle={() => onToggleSection(section.expiration)}
              />
            </div>
          ) : null}

          {chunkSectionRows(section.rows).map((chunk) =>
            chunk.kind === "spot" ? (
              <div
                key={chunk.key}
                aria-hidden
                style={{
                  height: ROW_HEIGHT,
                  ...spotStickyStyle(section.withBand),
                }}
              >
                <SpotRule spot={spot} showsPrice={false} />
              </div>
            ) : (
              <div
                key={chunk.key}
                data-chain-h-scroll
                className={METRIC_H_SCROLL_CLASS}
              >
                <table
                  className="w-full table-fixed border-separate border-spacing-0"
                  style={{ minWidth }}
                >
                  <caption className="sr-only">
                    {groupLabel}, {section.label}
                  </caption>
                  {columnWidths}
                  <tbody>
                    {chunk.rows.map((item) => (
                      <MetricsRow
                        key={item.key}
                        applyHref={applyHref}
                        metrics={columns}
                        indexOffset={indexOffset}
                        leg={side === "call" ? item.row.call : item.row.put}
                        row={item.row}
                        rowIndex={item.strikeIndex}
                        side={side}
                        // The row's own expiration, not the selected one: a
                        // stacked chain shows several, and a quote must name the
                        // contract it would actually buy.
                        expirationLabel={item.expirationLabel}
                        freshnessByMetric={freshnessByMetric}
                        canTrade={canTrade}
                        hasGroup={hasGroup}
                        isTicketFull={isTicketFull}
                        selectedSides={selectedSidesFor(
                          selected,
                          side === "call" ? item.row.call : item.row.put,
                        )}
                        onQuoteSelect={onQuoteSelect}
                        onFocusCell={onFocusCell}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ),
          )}
        </div>
      ))}

      {window.trailingRows > 0 ? (
        <div aria-hidden style={{ height: window.trailingRows * ROW_HEIGHT }} />
      ) : null}
    </Box>
  );
};

/**
 * The middle of the chain: each side's bid and ask, with the strike between them.
 *
 * In flow rather than an overlay on top of the metrics, so the buttons and the
 * keyboard stay on the real cells. It is the one column that never scrolls
 * horizontally, which is the point: the only cells a user acts on cannot be
 * scrolled away from the strike they belong to.
 */
const PinColumn = ({
  sideFilter,
  metrics,
  window,
  spot,
  showsSectionLabel,
  onToggleSection,
  isSpotUnavailable,
  freshnessByMetric,
  canTrade,
  hasGroup,
  applyHref,
  selected,
  isTicketFull,
  focusCell,
  onQuoteSelect,
  onFocusCell,
}: {
  sideFilter: ChainSideFilter;
  metrics: VisibleMetrics;
  window: ReturnType<typeof windowSections>;
  spot: number | undefined;
  /** Whether the pin is the outermost column, and so names each band. */
  showsSectionLabel: boolean;
  onToggleSection: (expiration: string) => void;
  isSpotUnavailable: boolean;
  freshnessByMetric: Readonly<Record<string, ValueFreshness>>;
  canTrade: boolean;
  hasGroup: boolean;
  applyHref: string;
  selected: readonly QuoteCellSelection[];
  isTicketFull: boolean;
  focusCell: FocusCell;
  onQuoteSelect: OptionsChainGridProps["onQuoteSelect"];
  onFocusCell: (cell: FocusCell) => void;
}) => {
  const showsBothSides = sideFilter === "both";
  const quoted = metrics.sticky;
  const callIndexOffset = pinIndexOffset(
    metrics,
    pinEdgeFor("call", sideFilter),
  );
  const putIndexOffset = pinIndexOffset(metrics, pinEdgeFor("put", sideFilter));
  const columnCount = 1 + quoted.length * (showsBothSides ? 2 : 1);
  const widthRem =
    STRIKE_WIDTH_REM + widthRemOf(quoted) * (showsBothSides ? 2 : 1);

  const columnWidths = (
    <colgroup>
      {showsBothSides
        ? quoted.map((metric) => (
            <col
              key={`call-${metric.id}`}
              style={{ width: `${metric.widthRem}rem` }}
            />
          ))
        : null}
      <col style={{ width: `${STRIKE_WIDTH_REM}rem` }} />
      {quoted.map((metric) => (
        <col
          key={`side-${metric.id}`}
          style={{ width: `${metric.widthRem}rem` }}
        />
      ))}
    </colgroup>
  );

  /**
   * Names one side, over the pair it belongs to, so the split is stated rather
   * than implied. The rule closes on the pair's outer edge — the inner one is
   * drawn by the strike column's own cells, and only one of two cells meeting
   * there may draw it or `border-separate` leaves two hairlines side by side.
   */
  const groupCell = (side: ChainSide) => (
    <th
      scope="colgroup"
      colSpan={quoted.length}
      className={cn(
        "whitespace-nowrap border-t border-b px-1.5",
        side === "call" && showsBothSides
          ? "border-l text-right"
          : "border-r text-left",
        hairlineBorderClass,
      )}
      style={{ ...groupHeaderCell, background: pinnedBackground }}
    >
      <Text size="1" weight="bold" color="gray">
        {side === "call" ? "Calls" : "Puts"}
      </Text>
    </th>
  );

  /* Centred over their values, so the pair reads as one labelled block. */
  const quoteHeaders = (side: ChainSide) =>
    quoted.map((metric) => (
      <th
        key={`${side}-${metric.id}`}
        scope="col"
        className={cn(
          "whitespace-nowrap border-b px-1.5 text-center",
          hairlineBorderClass,
        )}
        style={{ ...columnHeaderCell, background: pinnedBackground }}
      >
        <Text size="1" color="gray" weight="medium">
          {metric.label}
        </Text>
      </th>
    ));

  return (
    <Box
      data-chain-pin
      className="shrink-0 grow-0 overflow-x-clip"
      // Pin width is the rem sum of what it holds, so the metrics either side
      // get whatever is left.
      style={{ width: `${widthRem}rem` }}
    >
      <table className="sticky top-0 z-[4] w-full table-fixed border-separate border-spacing-0">
        <caption className="sr-only">Strikes</caption>
        {columnWidths}
        <thead>
          <tr style={{ height: GROUP_HEADER_HEIGHT }}>
            {showsBothSides ? groupCell("call") : null}
            <th
              aria-hidden
              className={cn("border-x border-t border-b", hairlineBorderClass)}
              style={{ ...groupHeaderCell, background: pinnedBackground }}
            />
            {sideFilter === "both" ? groupCell("put") : groupCell(sideFilter)}
          </tr>
          <tr style={{ height: COLUMN_HEADER_HEIGHT }}>
            {showsBothSides ? quoteHeaders("call") : null}
            <th
              scope="col"
              className={cn(
                "border-x border-b px-1.5 text-center",
                hairlineBorderClass,
              )}
              style={{ ...columnHeaderCell, background: pinnedBackground }}
            >
              {isSpotUnavailable ? (
                <Tooltip content="Underlying price unavailable, so the at-the-money and in-the-money markers are hidden.">
                  <Text size="1" color="orange" weight="medium">
                    Strike
                  </Text>
                </Tooltip>
              ) : (
                <Text size="1" color="gray" weight="medium">
                  Strike
                </Text>
              )}
            </th>
            {sideFilter === "both"
              ? quoteHeaders("put")
              : quoteHeaders(sideFilter)}
          </tr>
        </thead>
      </table>

      {window.leadingRows > 0 ? (
        <div aria-hidden style={{ height: window.leadingRows * ROW_HEIGHT }} />
      ) : null}

      {window.visible.map((section) => (
        <table
          key={section.expiration}
          data-chain-section={section.expiration}
          className="w-full table-fixed border-separate border-spacing-0"
        >
          <caption className="sr-only">Strikes, {section.label}</caption>
          {columnWidths}
          <tbody>
            {section.withBand ? (
              <tr style={{ height: ROW_HEIGHT }}>
                <td
                  colSpan={columnCount}
                  className={cn("border-b p-0", hairlineBorderClass)}
                  style={bandStickyStyle}
                >
                  <BandControl
                    label={section.label}
                    isCollapsed={section.isCollapsed}
                    showsLabel={showsSectionLabel}
                    onToggle={() => onToggleSection(section.expiration)}
                  />
                </td>
              </tr>
            ) : null}

            {section.rows.map((item) =>
              item.kind === "spot" ? (
                <tr key={item.key} style={{ height: ROW_HEIGHT }}>
                  {showsBothSides ? (
                    <td
                      aria-hidden
                      colSpan={quoted.length}
                      className="p-0"
                      style={spotStickyStyle(section.withBand)}
                    >
                      <SpotRule spot={spot} showsPrice={false} />
                    </td>
                  ) : null}
                  {/*
                    Announced, unlike the rule the metric panes draw: this cell
                    is the only place the underlying price is written, and it is
                    what puts the strikes above and below it in context.
                  */}
                  <th
                    scope="row"
                    className="p-0"
                    style={spotStickyStyle(section.withBand)}
                  >
                    <SpotRule spot={spot} showsPrice />
                    <Text className="sr-only"> underlying price</Text>
                  </th>
                  <td
                    aria-hidden
                    colSpan={quoted.length}
                    className="p-0"
                    style={spotStickyStyle(section.withBand)}
                  >
                    <SpotRule spot={spot} showsPrice={false} />
                  </td>
                </tr>
              ) : (
                <PinRow
                  key={item.key}
                  stickyMetrics={quoted}
                  sideFilter={sideFilter}
                  callIndexOffset={callIndexOffset}
                  putIndexOffset={putIndexOffset}
                  row={item.row}
                  rowIndex={item.strikeIndex}
                  expirationLabel={item.expirationLabel}
                  freshnessByMetric={freshnessByMetric}
                  canTrade={canTrade}
                  hasGroup={hasGroup}
                  applyHref={applyHref}
                  isTicketFull={isTicketFull}
                  callSelectedSides={selectedSidesFor(selected, item.row.call)}
                  putSelectedSides={selectedSidesFor(selected, item.row.put)}
                  focusCell={focusCell}
                  onQuoteSelect={onQuoteSelect}
                  onFocusCell={onFocusCell}
                />
              ),
            )}
          </tbody>
        </table>
      ))}

      {window.trailingRows > 0 ? (
        <div aria-hidden style={{ height: window.trailingRows * ROW_HEIGHT }} />
      ) : null}
    </Box>
  );
};

export const OptionsChainGrid = ({
  sections,
  metrics,
  symbol,
  freshnessByMetric,
  canTrade,
  hasGroup,
  applyHref,
  selected,
  isTicketFull,
  isDelayedFeed,
  realTimeHref,
  isGreeksUnavailable,
  isSpotUnavailable,
  spot,
  sideFilter,
  onQuoteSelect,
}: OptionsChainGridProps) => {
  /**
   * Expirations the user has folded away, by date. Local because it is view
   * state about what is on screen now — persisting it would keep resurrecting
   * choices about dates that have since expired.
   */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const toggleSection = useCallback((expiration: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (!next.delete(expiration)) {
        next.add(expiration);
      }
      return next;
    });
  }, []);

  const visualSections = useMemo(
    () => toVisualSections(sections, collapsed, spot),
    [sections, collapsed, spot],
  );

  /**
   * The ladders alone, and only the ones on screen: keyboard navigation steps
   * strikes rather than bands, and must not walk into a collapsed section.
   */
  const rows = useMemo(
    () =>
      visualSections.flatMap((section) =>
        section.rows.flatMap((item) =>
          item.kind === "strike" ? [item.row] : [],
        ),
      ),
    [visualSections],
  );

  /** The one element that scrolls vertically, so rows cannot come apart. */
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [viewportHeight, setViewportHeight] = useState(400);
  /**
   * Which sections are mounted. Held as the range itself rather than the scroll
   * position, because that is the only thing a scroll can change about the
   * render: a gesture crosses a row every frame or two but enters a new section
   * far more rarely, so keying on rows re-rendered the whole grid for nothing.
   */
  const [range, setRange] = useState<SectionRange>({
    startIndex: 0,
    endIndex: 0,
  });

  /**
   * Read by the scroll listener without becoming dependencies of it —
   * re-subscribing on every change would drop events mid-gesture.
   */
  const viewportHeightRef = useRef(viewportHeight);
  viewportHeightRef.current = viewportHeight;
  const sectionsRef = useRef(visualSections);
  sectionsRef.current = visualSections;
  const scrollTopRef = useRef(0);
  /** Where both sides' metrics are scrolled to, which is always the same place. */
  const scrollLeftRef = useRef(0);

  /**
   * Vertical scroll is handled outside React on purpose.
   *
   * Nothing has to move: one scroller carries all three columns, and sticky
   * positioning is the browser's job. React is told only when the mounted set of
   * sections changes, which is once per section crossed rather than once per row,
   * so most of a gesture costs no render at all.
   */
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }
    const onScroll = () => {
      const top = scroller.scrollTop;
      if (top === scrollTopRef.current) {
        return;
      }
      scrollTopRef.current = top;
      const next = sectionRange(
        sectionsRef.current,
        top,
        viewportHeightRef.current,
      );
      setRange((current) =>
        current.startIndex === next.startIndex &&
        current.endIndex === next.endIndex
          ? current
          : next,
      );
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, []);

  /**
   * Horizontal scroll is the one axis that has to be mirrored: each side owns its
   * own scroller, and every run of strikes within it, so that the pin between them
   * can stay put. A frame of lag here shows as columns lagging, which is far less
   * visible than rows splitting apart.
   *
   * Delegated from the scroller, which is always mounted, so that virtualization
   * can swap runs in and out without re-subscribing. `scroll` does not bubble, but
   * a capturing listener still sees it on the way down.
   */
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }
    const onScroll = (event: Event) => {
      const source = event.target;
      if (
        !(source instanceof HTMLElement) ||
        !source.hasAttribute("data-chain-h-scroll")
      ) {
        return;
      }
      const left = source.scrollLeft;
      if (left === scrollLeftRef.current) {
        return;
      }
      scrollLeftRef.current = left;
      // Skipping the panes already there is what stops the scroll events these
      // writes trigger from cascading.
      for (const pane of scroller.querySelectorAll<HTMLElement>(
        "[data-chain-h-scroll]",
      )) {
        if (pane !== source && pane.scrollLeft !== left) {
          pane.scrollLeft = left;
        }
      }
    };
    scroller.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    });
    return () =>
      scroller.removeEventListener("scroll", onScroll, { capture: true });
  }, []);

  const [focusCell, setFocusCell] = useState<FocusCell>({
    rowIndex: 0,
    side: "call",
    metricIndex: 0,
  });

  /*
    Only the visible slice is mounted: `ALL` strikes on a mega-cap is ~350 rows
    with up to four quote buttons each.

    Measured from the scroller, which is up whatever the side filter is and
    whatever the columns are, so the window can never be sized from a detached
    element and come out too short to reach the next expiration.
  */
  useEffect(() => {
    const element = scrollerRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => {
      if (element.clientHeight > 0) {
        setViewportHeight(element.clientHeight);
      }
    });
    observer.observe(element);
    setViewportHeight(element.clientHeight);
    return () => observer.disconnect();
  }, []);

  /*
    The range is only recomputed by the scroll listener, so anything else that
    moves the sections under it — new rows, a fold, a resize — has to ask again
    from where the reader actually is.
  */
  useEffect(() => {
    const next = sectionRange(
      visualSections,
      scrollTopRef.current,
      viewportHeight,
    );
    setRange((current) =>
      current.startIndex === next.startIndex &&
      current.endIndex === next.endIndex
        ? current
        : next,
    );
  }, [visualSections, viewportHeight]);

  const window = useMemo(
    () => windowSections(visualSections, range),
    [visualSections, range],
  );

  /*
    A run of strikes that has just been mounted starts at the left, so it is
    placed before it is painted — otherwise scrolling down through a chain that is
    scrolled right shows the first columns for a frame and then snaps.
  */
  // The effect reads the DOM rather than either value, so the rule cannot see
  // that they are what decide which runs are mounted.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the mounted runs follow the window
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const left = scrollLeftRef.current;
    if (!scroller || left === 0) {
      return;
    }
    for (const pane of scroller.querySelectorAll<HTMLElement>(
      "[data-chain-h-scroll]",
    )) {
      if (pane.scrollLeft !== left) {
        pane.scrollLeft = left;
      }
    }
  }, [visualSections, range]);

  const callOrder = orderedMetrics(metrics, pinEdgeFor("call", sideFilter));
  const putOrder = orderedMetrics(metrics, pinEdgeFor("put", sideFilter));

  /**
   * Columns that render a button, per side. Bid and ask sit at opposite ends of
   * the two panes, so the indexes differ and cannot be shared.
   */
  const actionableIndexes = useMemo(() => {
    const indexesFor = (ordered: readonly ChainMetric[]) =>
      ordered.reduce<number[]>((indexes, metric, index) => {
        if (metric.isQuoteAction) {
          indexes.push(index);
        }
        return indexes;
      }, []);
    return { call: indexesFor(callOrder), put: indexesFor(putOrder) };
  }, [callOrder, putOrder]);

  /** A cell can hold focus only when it renders a button to activate. */
  const canFocus = useCallback(
    (cell: FocusCell) => {
      if (!canTrade || !hasGroup) {
        return false;
      }
      const row = rows[cell.rowIndex];
      const leg = cell.side === "call" ? row?.call : row?.put;
      const ordered = cell.side === "call" ? callOrder : putOrder;
      const metric = ordered[cell.metricIndex];
      if (!leg || !metric?.isQuoteAction) {
        return false;
      }
      const hasPrice =
        metric.id === "bid"
          ? leg.quote.bid !== undefined
          : leg.quote.ask !== undefined;
      if (!hasPrice) {
        return false;
      }
      // Must match the cell's own rule, or focus lands on a value with no button.
      const isSelected = selected.some(
        (entry) => entry.symbol === leg.symbol && entry.side === metric.id,
      );
      return !isTicketFull || isSelected;
    },
    [rows, callOrder, putOrder, canTrade, hasGroup, isTicketFull, selected],
  );

  /**
   * Set by the arrow keys only, so mounting or refreshing the widget never steals
   * focus from elsewhere on the dashboard.
   */
  const isNavigatingRef = useRef(false);

  /**
   * Roving focus. Without it, Tab walks hundreds of identically shaped quote
   * buttons before it reaches anything else on the dashboard.
   */
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const next = nextFocusCell({
        key: event.key,
        current: focusCell,
        actionableIndexes:
          focusCell.side === "call"
            ? actionableIndexes.call
            : actionableIndexes.put,
        crossSideIndexes:
          focusCell.side === "call"
            ? actionableIndexes.put
            : actionableIndexes.call,
        rowCount: rows.length,
        canFocus,
      });
      if (!next) {
        return;
      }
      isNavigatingRef.current = true;
      event.preventDefault();
      setFocusCell(next);
      // Keep the newly focused row inside the window so its button can mount.
      const element = scrollerRef.current;
      if (element) {
        const top = scrollTopForStrike(visualSections, next.rowIndex);
        if (top < element.scrollTop) {
          element.scrollTop = top;
        } else if (
          top + ROW_HEIGHT >
          element.scrollTop + element.clientHeight
        ) {
          element.scrollTop = top + ROW_HEIGHT - element.clientHeight;
        }
      }
    },
    [focusCell, actionableIndexes, rows.length, canFocus, visualSections],
  );

  const isCompactHeader = metrics.scrollable.length > 4;

  /**
   * Snapped to a column that renders a button, so a column change can never leave
   * the grid without a tab stop.
   */
  const effectiveFocusCell = useMemo(() => {
    const indexes =
      focusCell.side === "call"
        ? actionableIndexes.call
        : actionableIndexes.put;
    return indexes.includes(focusCell.metricIndex)
      ? focusCell
      : { ...focusCell, metricIndex: indexes[0] ?? 0 };
  }, [focusCell, actionableIndexes]);

  /**
   * Places focus after the render that mounts the target row. Scrolling to a row
   * outside the window unmounts the old button first, so the DOM node has to be
   * looked up again rather than held in a ref.
   */
  useEffect(() => {
    if (!isNavigatingRef.current) {
      return;
    }
    // Outside the mounted sections the button does not exist yet. Scrolling
    // mounts more and re-runs this effect.
    const target = scrollerRef.current?.querySelector<HTMLButtonElement>(
      `[data-chain-cell="${cellAddress(effectiveFocusCell)}"]`,
    );
    if (target) {
      target.focus();
      isNavigatingRef.current = false;
    }
  }, [effectiveFocusCell]);

  /** What both kinds of column need to render the same rows at the same heights. */
  const sharedProps = {
    window,
    spot,
    onToggleSection: toggleSection,
    freshnessByMetric,
    canTrade,
    hasGroup,
    applyHref,
    selected,
    isTicketFull,
    onQuoteSelect,
    onFocusCell: setFocusCell,
  };

  const paneProps = {
    ...sharedProps,
    isCompactHeader,
    symbol,
    isDelayedFeed,
    realTimeHref,
    isGreeksUnavailable,
  };

  const hasScrollableMetrics = metrics.scrollable.length > 0;
  const showsBothSides = sideFilter === "both";
  /**
   * The pin takes the width it needs and the metrics divide what is left, so the
   * cells a user acts on are never squeezed by how many metrics are on. In the
   * narrow fit there are no scrollable metrics to divide it with, so the pin is
   * simply centred.
   */
  const layoutClassName = !hasScrollableMetrics
    ? "flex justify-center"
    : showsBothSides
      ? "grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]"
      : "grid grid-cols-[auto_minmax(0,1fr)]";

  const metricsPane = (side: ChainSide) => (
    <MetricsPane
      side={side}
      columns={metrics.scrollable}
      indexOffset={paneIndexOffset(metrics, pinEdgeFor(side, sideFilter))}
      // The expiration is named by the outermost column, which is the calls'
      // metrics when both sides are up and the pin when only one is.
      showsSectionLabel={showsBothSides && side === "call"}
      {...paneProps}
    />
  );

  return (
    <Box
      data-chain-grid
      onKeyDown={onKeyDown}
      flexGrow="1"
      minHeight="0"
      overflow="hidden"
      className="relative flex min-h-0 flex-col"
    >
      {/*
        The one vertical scroller. `overscroll-behavior: none` stops a scroll at
        either end of the chain from chaining out to the dashboard behind it.
      */}
      <Box
        ref={scrollerRef}
        data-chain-scroller
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-none"
      >
        <Box className={cn("items-start", layoutClassName)}>
          {/*
            Calls lead only when both sides are up, where the strike column
            belongs between them. Showing one side puts the pin first instead, so
            the strikes are read before the prices rather than after them.
          */}
          {showsBothSides && hasScrollableMetrics ? metricsPane("call") : null}

          <PinColumn
            sideFilter={sideFilter}
            metrics={metrics}
            isSpotUnavailable={isSpotUnavailable}
            showsSectionLabel={!(showsBothSides && hasScrollableMetrics)}
            focusCell={effectiveFocusCell}
            {...sharedProps}
          />

          {hasScrollableMetrics
            ? metricsPane(sideFilter === "both" ? "put" : sideFilter)
            : null}
        </Box>
      </Box>
    </Box>
  );
};
